/**
 * Lógica de domínio pura do Elo (sem storage). Deriva o "estado da equipe" por
 * criança e o mapa de responsabilidade da escada entre disciplinas.
 */
import type { Alimento, EspecialidadeProf, Oferta, PassoEscada } from "../types";
import { PassoEscada as Passo } from "../types";
import { calcularDerivados } from "../domain";
import {
  DEGRAU_TO_MAX,
  type DerivadosEquipe,
  type ItemResponsabilidade,
  type NotaSessao,
  type Profissional,
} from "./types";

const DIA_MS = 24 * 60 * 60 * 1000;

/** Notas de uma criança, mais recentes primeiro (para a timeline). */
export function timelineDaCrianca(
  notas: NotaSessao[],
  criancaId: string,
): NotaSessao[] {
  return notas
    .filter((n) => n.crianca_id === criancaId)
    .slice()
    .sort((a, b) => b.data_sessao.localeCompare(a.data_sessao));
}

/**
 * Mapa de responsabilidade (didático, §3 do estudo): TO destrava 0–4, fono 5–9.
 * Diz "de quem é o gargalo agora" a partir do degrau atual de um alimento.
 */
export function disciplinaDoDegrau(passo: PassoEscada): EspecialidadeProf {
  return (passo as number) <= (DEGRAU_TO_MAX as number) ? "to" : "fono";
}

/** Estado da equipe por criança: disciplinas que acompanham + dias sem nota de cada. */
export function calcularDerivadosEquipe(
  criancaId: string,
  notas: NotaSessao[],
  agora: Date = new Date(),
): DerivadosEquipe {
  const daCrianca = notas
    .filter((n) => n.crianca_id === criancaId)
    .sort((a, b) => a.data_sessao.localeCompare(b.data_sessao));

  const dias_sem_nota: Partial<Record<EspecialidadeProf, number>> = {};
  for (const n of daCrianca) {
    const d = Math.floor((agora.getTime() - new Date(n.data_sessao).getTime()) / DIA_MS);
    const atual = dias_sem_nota[n.especialidade];
    // guarda o MENOR (nota mais recente daquela disciplina)
    if (atual === undefined || d < atual) dias_sem_nota[n.especialidade] = d;
  }

  const ultima = daCrianca[daCrianca.length - 1] ?? null;
  return {
    crianca_id: criancaId,
    n_notas: daCrianca.length,
    ultima_sessao: ultima?.data_sessao ?? null,
    disciplinas: Object.keys(dias_sem_nota) as EspecialidadeProf[],
    dias_sem_nota,
  };
}

/**
 * Disciplinas que não registram nota há >= cadência (lembrete de coordenação).
 * Considera só as disciplinas que já apareceram pelo menos uma vez.
 */
export function disciplinasParaRetomar(
  d: DerivadosEquipe,
  cadenciaDias: number,
): Array<{ especialidade: EspecialidadeProf; dias: number }> {
  return (Object.entries(d.dias_sem_nota) as [EspecialidadeProf, number][])
    .filter(([, dias]) => dias >= cadenciaDias)
    .map(([especialidade, dias]) => ({ especialidade, dias }))
    .sort((a, b) => b.dias - a.dias);
}

/** Profissionais não arquivados de uma especialidade (para sugerir autor da nota). */
export function profissionaisDaEspecialidade(
  profissionais: Profissional[],
  esp: EspecialidadeProf,
): Profissional[] {
  return profissionais.filter((p) => !p.arquivado && p.especialidade === esp);
}

/**
 * Painel de responsabilidade: os alimentos que a equipe está trabalhando (citados
 * em notas) e de quem é a vez de evoluí-los — derivado do degrau atual. O degrau
 * vem da escada compartilhada (ofertas, via calcularDerivados); se ainda não houver
 * oferta, cai no maior degrau observado nas notas. Mais "parados" primeiro.
 */
export function painelResponsabilidade(
  criancaId: string,
  notas: NotaSessao[],
  ofertas: Oferta[],
  alimentos: Alimento[],
  agora: Date = new Date(),
): ItemResponsabilidade[] {
  const porAlimento = new Map<string, NotaSessao[]>();
  for (const n of notas) {
    if (n.crianca_id !== criancaId || !n.alimento_id) continue;
    const arr = porAlimento.get(n.alimento_id) ?? [];
    arr.push(n);
    porAlimento.set(n.alimento_id, arr);
  }

  const itens: ItemResponsabilidade[] = [];
  for (const [aid, ns] of porAlimento) {
    if (!alimentos.some((a) => a.id === aid)) continue;
    const d = calcularDerivados(aid, ofertas, agora);
    const obs = ns
      .map((n) => n.degrau_observado)
      .filter((x): x is PassoEscada => x !== null);
    const degrau_atual: PassoEscada | null =
      d.passo_atual ?? (obs.length ? (Math.max(...obs) as PassoEscada) : null);
    const datas = ns.map((n) => n.data_sessao).sort();
    const ultima = datas.length ? datas[datas.length - 1] : null;
    itens.push({
      alimento_id: aid,
      degrau_atual,
      responsavel: degrau_atual === null ? null : disciplinaDoDegrau(degrau_atual),
      disciplinas: [...new Set(ns.map((n) => n.especialidade))],
      ultima_nota: ultima,
      dias_desde_ultima: ultima
        ? Math.floor((agora.getTime() - new Date(ultima).getTime()) / DIA_MS)
        : null,
    });
  }
  return itens.sort((a, b) => (b.dias_desde_ultima ?? 1e9) - (a.dias_desde_ultima ?? 1e9));
}

/** Um resultado de sessão vira Oferta quando há alimento + degrau observado. */
export function resultadoGeraOferta(n: {
  alimento_id: string | null;
  degrau_observado: PassoEscada | null;
}): boolean {
  return n.alimento_id !== null && n.degrau_observado !== null;
}

export const ESCADA_MIN = Passo.NaoToleraNaMesa; // 0
export const ESCADA_MAX = Passo.ComeAPorcao; // 9
